import { z } from "zod";
import {
	isOrgScopedConfigurationTypesQuery,
	mergeConfigurationTypesById,
	ORG_SCOPED_CONFIGURATION_TYPES_FETCH_CAP,
	sliceConfigurationTypesPage,
	sortConfigurationTypes,
	stripEnabledParam,
} from "@/utils/configuration-type-list/configuration-type-org-scoped-list.utils";
import { isRequestAborted } from "@/utils/is-request-aborted";
import { shouldShowOrgAdminJobTypesTableRow } from "@/utils/job-type-detail/job-type-detail.utils";
import { apiService, organizationIdHeader } from "../api.service";
import {
	appendAttachmentRequirementTagId,
	createTypeAttachmentRequirementTag,
	deleteTypeAttachmentRequirementTag,
	editTypeAttachmentRequirementTag,
	getTypeAttachmentRequirement,
	putTypeAttachmentRequirement,
} from "../shared/attachment-requirement.api";
import { appendEntityDataQueriesToQueryParams } from "../form-definition/entity-data-query-params";
import {
	type GeometrySourceDisplay,
	GeometrySourcesArraySchema,
	type GpsMethodDisplay,
	GpsMethodsArraySchema,
} from "../shared/geometry-catalog.types";
import {
	type AuditLogDisplay,
	AuditLogDisplaySchema,
	type CreateJobInfo,
	CreateJobInfoSchema,
	type CreateJobTypeInfo,
	CreateJobTypeInfoSchema,
	type CreateNoteInfo,
	CreateNoteInfoSchema,
	type EditJobFileInfo,
	type EditJobTypeInfo,
	EditJobTypeInfoSchema,
	type FeatureCollection,
	FeatureCollectionSchema,
	type FileDisplay,
	FileDisplayArraySchema,
	type GetJobStatusesResponse,
	JobApiError,
	type JobAssetAssociationParams,
	type JobAssetInfo,
	JobAssetInfoArraySchema,
	type JobAssignmentDisplay,
	JobAssignmentDisplaySchema,
	type JobAssignmentParams,
	type JobDisplay,
	JobDisplaySchema,
	type JobDisplayWithUsers,
	JobDisplayWithUsersSchema,
	type JobFileParams,
	type JobNoteParams,
	type JobParams,
	type JobRecordAssociationParams,
	type JobRecordInfo,
	JobRecordInfoArraySchema,
	JobStatusesArraySchema,
	type JobType,
	JobTypeDisplaySchema,
	JobTypesArraySchema,
	type JobTypesListParams,
	type JobTypesQueryOptionsInput,
	type NoteDisplay,
	NoteDisplayArraySchema,
	normalizeJobTypesQueryOptions,
	type ServiceResult,
	ServiceResultSchema,
	type SubContractorDisplay,
	SubContractorDisplaySchema,
	type SubContractorParams,
	type UpdateJobInfo,
	UpdateJobInfoSchema,
	type UpdateJobStatusInfo,
	UpdateJobStatusInfoSchema,
} from "./job.types";

// Array schemas for list responses
const JobsArray = z.array(JobDisplaySchema);
const JobAssignmentsArray = z.array(JobAssignmentDisplaySchema);
const SubContractorsArray = z.array(SubContractorDisplaySchema);
const AuditLogArray = z.array(AuditLogDisplaySchema);

type OrderByQueryParams = {
	OrderBy?: { Fields?: string[]; Descending?: boolean };
	[key: string]: unknown;
};

/**
 * Flattens organization query options with nested OrderBy for ASP.NET binding.
 */
function toOrderByQueryParams(
	params?: OrderByQueryParams,
):
	| Record<string, string | number | boolean | string[] | undefined>
	| undefined {
	if (!params) return undefined;
	const flat: Record<string, string | number | boolean | string[] | undefined> =
		{};
	const { OrderBy, ...rest } = params;
	for (const [k, v] of Object.entries(rest)) {
		if (v !== undefined) flat[k] = v as string | number | boolean | string[];
	}
	if (OrderBy?.Fields !== undefined) flat["OrderBy.Fields"] = OrderBy.Fields;
	if (OrderBy?.Descending !== undefined)
		flat["OrderBy.Descending"] = OrderBy.Descending;
	return Object.keys(flat).length ? flat : undefined;
}

/**
 * Flattens {@link JobFileParams} for ASP.NET query-string binding.
 */
function toJobFileQueryParams(
	params?: JobFileParams,
):
	| Record<string, string | number | boolean | string[] | undefined>
	| undefined {
	return toOrderByQueryParams(params);
}

/**
 * Flattens {@link JobNoteParams} for ASP.NET query-string binding.
 */
function toJobNoteQueryParams(
	params?: JobNoteParams,
):
	| Record<string, string | number | boolean | string[] | undefined>
	| undefined {
	return toOrderByQueryParams(params);
}

/**
 * Flattens job association list/count query params for ASP.NET binding.
 */
function toJobAssociationQueryParams(
	params?: JobAssetAssociationParams | JobRecordAssociationParams,
):
	| Record<string, string | number | boolean | string[] | undefined>
	| undefined {
	return toOrderByQueryParams(params);
}

/** Form copy on a real job type can outlast the shared API timeout. */
const DUPLICATE_JOB_TYPE_TIMEOUT_MS = 60_000;

const JOB_TYPE_ORDER_BY_ACCESSOR: Record<
	string,
	(row: JobType) => string | number | null | undefined
> = {
	Name: (row) => row.name ?? "",
	Description: (row) => row.description ?? "",
	Id: (row) => row.id ?? "",
};

function filterOrgAdminJobTypesTableRows(rows: JobType[]): JobType[] {
	return rows.filter(shouldShowOrgAdminJobTypesTableRow);
}

function sortJobTypes(rows: JobType[], input?: JobTypesListParams): JobType[] {
	return sortConfigurationTypes(
		rows,
		input,
		JOB_TYPE_ORDER_BY_ACCESSOR,
		"Name",
	);
}

/**
 * Flatten JobParams so nested BoundingBox is serialized as dot notation
 * (e.g. BoundingBox.MinLatitude=49.48) instead of bracket notation
 * (BoundingBox[MinLatitude]=49.48), which the API expects.
 */
function toJobQueryParams(
	params?: JobParams,
):
	| Record<string, string | number | boolean | string[] | undefined>
	| undefined {
	if (!params) return undefined;
	const flat: Record<string, string | number | boolean | string[] | undefined> =
		{};
	const { BoundingBox, EntityDataQueries, ...rest } = params;
	if (BoundingBox) {
		if (BoundingBox.MinLatitude !== undefined)
			flat["BoundingBox.MinLatitude"] = BoundingBox.MinLatitude;
		if (BoundingBox.MaxLatitude !== undefined)
			flat["BoundingBox.MaxLatitude"] = BoundingBox.MaxLatitude;
		if (BoundingBox.MinLongitude !== undefined)
			flat["BoundingBox.MinLongitude"] = BoundingBox.MinLongitude;
		if (BoundingBox.MaxLongitude !== undefined)
			flat["BoundingBox.MaxLongitude"] = BoundingBox.MaxLongitude;
	}
	for (const [k, v] of Object.entries(rest)) {
		if (v !== undefined) flat[k] = v as string | number | boolean | string[];
	}
	appendEntityDataQueriesToQueryParams(flat, EntityDataQueries);
	return Object.keys(flat).length ? flat : undefined;
}

export class JobService {
	// ==================== CORE JOB OPERATIONS ====================

	/**
	 * Get jobs with optional filtering and pagination
	 * GET /jobs/{start}/{count}
	 */
	async getJobs(
		start: number = 1,
		count: number = 10,
		params?: JobParams,
	): Promise<JobDisplay[]> {
		try {
			const response = await apiService.get<JobDisplay[]>(
				`/jobs/${start}/${count}`,
				{
					params: toJobQueryParams(params),
					headers: organizationIdHeader(params?.OrganizationId),
				},
			);

			return JobsArray.parse(response);
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to get jobs: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get jobs count with optional filtering
	 * GET /jobs/count
	 */
	async getJobsCount(
		params?: JobParams,
		options?: { signal?: AbortSignal },
	): Promise<number> {
		try {
			const response = await apiService.get<number>("/jobs/count", {
				params: toJobQueryParams(params),
				headers: organizationIdHeader(params?.OrganizationId),
				signal: options?.signal,
			});

			return z.number().parse(response);
		} catch (error) {
			if (isRequestAborted(error)) throw error;
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to get jobs count: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get a single job by ID
	 * GET /jobs/{id}
	 */
	async getJob(
		id: string,
		options?: { organizationId?: string },
	): Promise<JobDisplayWithUsers> {
		try {
			const response = await apiService.get<JobDisplayWithUsers>(
				`/jobs/${id}`,
				{
					headers: organizationIdHeader(options?.organizationId),
				},
			);

			return JobDisplayWithUsersSchema.parse(response);
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to get job ${id}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Create a new job
	 * POST /jobs
	 */
	async createJob(data: CreateJobInfo): Promise<ServiceResult> {
		try {
			// Enhanced validation with detailed error messages
			const validatedData = CreateJobInfoSchema.parse(data);

			const response = await apiService.post<ServiceResult>(
				"/jobs",
				{
					...(validatedData.name ? { name: validatedData.name } : {}),
					...(validatedData.description
						? { description: validatedData.description }
						: {}),
					...(validatedData.typeId ? { typeId: validatedData.typeId } : {}),
					...(validatedData.organizationUserIds?.length
						? { organizationUserIds: validatedData.organizationUserIds }
						: {}),
					subOrganizationId: validatedData.subOrganizationId ?? undefined,
					...(validatedData.subContractorIds?.length
						? { subContractorIds: validatedData.subContractorIds }
						: {}),
					...(validatedData.latitude !== undefined
						? { latitude: validatedData.latitude }
						: {}),
					...(validatedData.longitude !== undefined
						? { longitude: validatedData.longitude }
						: {}),
					...(validatedData.geometrySource !== undefined
						? { geometrySource: validatedData.geometrySource }
						: {}),
					...(validatedData.gpsMethod !== undefined
						? { gpsMethod: validatedData.gpsMethod }
						: {}),
					...(validatedData.formData != null
						? { formData: validatedData.formData }
						: {}),
				},
				{
					headers: organizationIdHeader(
						validatedData.organizationId ?? undefined,
					),
				},
			);

			const result = ServiceResultSchema.parse(response);

			if (!result.success) {
				throw JobApiError.fromServiceResult(result);
			}

			return result;
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}

			// Enhanced error handling with specific error types
			if (error instanceof z.ZodError) {
				const fieldErrors = error.issues.map((err) => ({
					field: err.path.join("."),
					message: err.message,
				}));
				throw new JobApiError(
					`Validation failed: ${fieldErrors.map((e) => `${e.field}: ${e.message}`).join(", ")}`,
					error,
				);
			}

			throw new JobApiError(
				`Failed to create job: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Delete a job by ID
	 * DELETE /jobs/{id}
	 *
	 * @param id - The job ID to delete
	 * @param options - Optional organization scope (required for platform admin)
	 * @returns ServiceResult indicating success or failure
	 */
	async deleteJob(
		id: string,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const response = await apiService.delete<ServiceResult>(`/jobs/${id}`, {
				headers: organizationIdHeader(options?.organizationId),
			});

			const result = ServiceResultSchema.parse(response);

			if (!result.success) {
				throw JobApiError.fromServiceResult(result);
			}

			return result;
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to delete job ${id}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Update a job by ID
	 * PUT /jobs/{jobId}
	 *
	 * @param id - The job ID to update
	 * @param data - The update payload (EditJobInfo)
	 * @returns ServiceResult indicating success or failure
	 */
	async updateJob(id: string, data: UpdateJobInfo): Promise<ServiceResult> {
		try {
			const validatedData = UpdateJobInfoSchema.parse(data);

			const response = await apiService.put<ServiceResult>(
				`/jobs/${id}`,
				{
					...(validatedData.organizationId !== undefined
						? { organizationId: validatedData.organizationId }
						: {}),
					...(validatedData.name !== undefined
						? { name: validatedData.name }
						: {}),
					...(validatedData.description !== undefined
						? { description: validatedData.description }
						: {}),
					...(validatedData.statusId !== undefined
						? { statusId: validatedData.statusId }
						: {}),
					...(validatedData.typeId !== undefined
						? { typeId: validatedData.typeId }
						: {}),
					...(validatedData.latitude !== undefined
						? { latitude: validatedData.latitude }
						: {}),
					...(validatedData.longitude !== undefined
						? { longitude: validatedData.longitude }
						: {}),
					...(validatedData.geometrySource !== undefined
						? { geometrySource: validatedData.geometrySource }
						: {}),
					...(validatedData.gpsMethod !== undefined
						? { gpsMethod: validatedData.gpsMethod }
						: {}),
					...(validatedData.subOrganizationId !== undefined
						? { subOrganizationId: validatedData.subOrganizationId }
						: {}),
					...(validatedData.organizationUserIds !== undefined
						? { organizationUserIds: validatedData.organizationUserIds }
						: {}),
					...(validatedData.subContractorIds !== undefined
						? { subContractorIds: validatedData.subContractorIds }
						: {}),
					...(validatedData.formData !== undefined
						? { formData: validatedData.formData }
						: {}),
				},
				{
					headers: organizationIdHeader(
						validatedData.organizationId ?? undefined,
					),
				},
			);

			const result = ServiceResultSchema.parse(response);

			if (!result.success) {
				throw JobApiError.fromServiceResult(result);
			}

			return result;
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}

			if (error instanceof z.ZodError) {
				const fieldErrors = error.issues.map((err) => ({
					field: err.path.join("."),
					message: err.message,
				}));
				throw new JobApiError(
					`Validation failed: ${fieldErrors.map((e) => `${e.field}: ${e.message}`).join(", ")}`,
					error,
				);
			}

			throw new JobApiError(
				`Failed to update job ${id}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Update a job's status
	 * PUT /jobs/{jobId}/status
	 *
	 * @param id - The job ID
	 * @param data - The status update payload
	 * @returns ServiceResult indicating success or failure
	 */
	async updateJobStatus(
		id: string,
		data: UpdateJobStatusInfo,
	): Promise<ServiceResult> {
		try {
			const validatedData = UpdateJobStatusInfoSchema.parse(data);

			const response = await apiService.put<ServiceResult>(
				`/jobs/${id}/status`,
				{
					...(validatedData.organizationId !== undefined
						? { organizationId: validatedData.organizationId }
						: {}),
					...(validatedData.statusId !== undefined
						? { statusId: validatedData.statusId }
						: {}),
				},
				{
					headers: organizationIdHeader(
						validatedData.organizationId ?? undefined,
					),
				},
			);

			const result = ServiceResultSchema.parse(response);

			if (!result.success) {
				throw JobApiError.fromServiceResult(result);
			}

			return result;
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}

			if (error instanceof z.ZodError) {
				const fieldErrors = error.issues.map((err) => ({
					field: err.path.join("."),
					message: err.message,
				}));
				throw new JobApiError(
					`Validation failed: ${fieldErrors.map((e) => `${e.field}: ${e.message}`).join(", ")}`,
					error,
				);
			}

			throw new JobApiError(
				`Failed to update job status ${id}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get valid statuses for a job
	 * GET /jobs/{id}/valid-statuses
	 *
	 * @returns Array of valid job statuses for the specified job
	 */
	async getJobValidStatuses(id: string): Promise<GetJobStatusesResponse> {
		try {
			const response = await apiService.get<GetJobStatusesResponse>(
				`/jobs/${id}/valid-statuses`,
			);

			return JobStatusesArraySchema.parse(response);
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to get valid statuses for job ${id}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * GET /jobs/types/count
	 */
	async getJobTypesCount(input?: JobTypesQueryOptionsInput): Promise<number> {
		try {
			const params = normalizeJobTypesQueryOptions(input);
			if (
				isOrgScopedConfigurationTypesQuery(params) &&
				params.Enabled === undefined
			) {
				const merged = await this.getOrgScopedJobTypesMerged(params);
				return merged.length;
			}

			return this.fetchJobTypesCountSingle(params);
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to get job types count: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	private async fetchJobTypesCountSingle(
		params: JobTypesListParams,
	): Promise<number> {
		const response = await apiService.get<number>("/jobs/types/count", {
			params,
		});
		return z.number().parse(response);
	}

	/**
	 * GET /jobs/types/{start}/{count}/export — `text/csv`
	 */
	async exportJobTypesCsv(
		start: number,
		count: number,
		input?: JobTypesQueryOptionsInput,
		columns?: string | null,
	): Promise<Blob> {
		try {
			const params: JobTypesListParams & { columns?: string } = {
				...normalizeJobTypesQueryOptions(input),
				...(columns != null && columns !== "" ? { columns } : {}),
			};
			return await apiService.get<Blob>(
				`/jobs/types/${start}/${count}/export`,
				{
					params,
					headers: {
						Accept: "text/csv",
					},
					responseType: "blob",
				},
			);
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to export job types: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * GET /jobs/types — returns all job types for the org, optionally scoped to a
	 * sub-organization. Replaces the count + paginated fetch for the create flow.
	 */
	async getJobTypesForCreate(
		organizationId?: string,
		subOrganizationId?: string,
	): Promise<JobType[]> {
		try {
			const response = await apiService.get<unknown>("/jobs/types", {
				headers: organizationIdHeader(organizationId),
				...(subOrganizationId ? { params: { subOrganizationId } } : {}),
			});
			return JobTypesArraySchema.parse(response);
		} catch (error) {
			if (error instanceof JobApiError) throw error;
			throw new JobApiError(
				`Failed to get job types: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Job types list — GET /jobs/types/{start}/{count}.
	 * Without `page`, calls GET /jobs/types/count then fetches `/jobs/types/1/{total}`.
	 */
	async getJobTypes(
		input?: JobTypesQueryOptionsInput,
		page?: { start: number; count: number },
	): Promise<JobType[]> {
		try {
			const params = normalizeJobTypesQueryOptions(input);
			if (page === undefined) {
				const total = await this.getJobTypesCount(input);
				if (total <= 0) return [];
				return this.fetchAllJobTypesPaged(total, params);
			}

			if (
				isOrgScopedConfigurationTypesQuery(params) &&
				params.Enabled === undefined
			) {
				const merged = await this.getOrgScopedJobTypesMerged(params);
				const sorted = sortJobTypes(merged, params);
				return sliceConfigurationTypesPage(sorted, page.start, page.count);
			}

			return this.fetchJobTypesPageSingle(page.start, page.count, params);
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to get job types: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/** Fetches all job types in batches of 100 to respect the API's per-request limit. */
	private async fetchAllJobTypesPaged(
		total: number,
		params: JobTypesListParams,
	): Promise<JobType[]> {
		const PAGE_SIZE = 100;
		const results: JobType[] = [];
		let fetched = 0;
		while (fetched < total) {
			const batch = await this.fetchJobTypesPageSingle(
				fetched + 1,
				Math.min(PAGE_SIZE, total - fetched),
				params,
			);
			results.push(...batch);
			fetched += batch.length;
			if (batch.length === 0) break;
		}
		return results;
	}

	/** Enabled + disabled org-scoped rows merged for configuration tables. */
	private async getOrgScopedJobTypesMerged(
		input?: JobTypesListParams,
	): Promise<JobType[]> {
		const baseParams = stripEnabledParam(input) ?? {};
		const enabledParams = { ...baseParams, Enabled: true };
		const disabledParams = { ...baseParams, Enabled: false };
		const [enabledCount, disabledCount] = await Promise.all([
			this.fetchJobTypesCountSingle(enabledParams),
			this.fetchJobTypesCountSingle(disabledParams),
		]);

		const [enabledRows, disabledRows] = await Promise.all([
			enabledCount > 0
				? this.fetchJobTypesPageSingle(
						1,
						Math.min(enabledCount, ORG_SCOPED_CONFIGURATION_TYPES_FETCH_CAP),
						enabledParams,
					)
				: Promise.resolve([]),
			disabledCount > 0
				? this.fetchJobTypesPageSingle(
						1,
						Math.min(disabledCount, ORG_SCOPED_CONFIGURATION_TYPES_FETCH_CAP),
						disabledParams,
					)
				: Promise.resolve([]),
		]);

		return filterOrgAdminJobTypesTableRows(
			mergeConfigurationTypesById(enabledRows, disabledRows),
		);
	}

	private async fetchJobTypesPageSingle(
		start: number,
		count: number,
		params: JobTypesListParams,
	): Promise<JobType[]> {
		const response = await apiService.get<unknown>(
			`/jobs/types/${start}/${count}`,
			{ params },
		);
		return JobTypesArraySchema.parse(response);
	}

	/**
	 * Get a single job type by id
	 * GET /jobs/types/{jobTypeId}
	 *
	 * `organizationId` — sent as `orgId` header so platform admins acting in Org Admin
	 * resolve the job type in the correct organization scope.
	 */
	async getJobTypeById(
		jobTypeId: string,
		options?: { subOrganizationId?: string; organizationId?: string },
	): Promise<JobType> {
		try {
			const subOrganizationId = options?.subOrganizationId;
			const organizationId = options?.organizationId;
			const response = await apiService.get<unknown>(
				`/jobs/types/${jobTypeId}`,
				{
					params:
						subOrganizationId && subOrganizationId.length > 0
							? { subOrganizationId }
							: undefined,
					headers:
						organizationId && organizationId.length > 0
							? { "Organization-id": organizationId }
							: undefined,
				},
			);

			return JobTypeDisplaySchema.parse(response);
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to get job type: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Update a job type
	 * PUT /jobs/types/{jobTypeId}
	 */
	async updateJobType(
		jobTypeId: string,
		data: EditJobTypeInfo,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const validated = EditJobTypeInfoSchema.parse(data);
			const organizationId = options?.organizationId;
			const response = await apiService.put<ServiceResult>(
				`/jobs/types/${jobTypeId}`,
				validated,
				{
					headers:
						organizationId !== undefined
							? { "organization-id": organizationId }
							: undefined,
				},
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw JobApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			if (error instanceof z.ZodError) {
				const fieldErrors = error.issues.map((err) => ({
					field: err.path.join("."),
					message: err.message,
				}));
				throw new JobApiError(
					`Validation failed: ${fieldErrors.map((e) => `${e.field}: ${e.message}`).join(", ")}`,
					error,
				);
			}
			throw new JobApiError(
				`Failed to update job type: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Enable a job type for an organization
	 * PUT /jobs/types/{jobTypeId}/enable
	 */
	async enableJobType(
		jobTypeId: string,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const organizationId = options?.organizationId;
			const response = await apiService.put<ServiceResult>(
				`/jobs/types/${jobTypeId}/enable`,
				undefined,
				{
					headers:
						organizationId !== undefined
							? { "organization-id": organizationId }
							: undefined,
				},
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw JobApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to enable job type: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Disable a job type for an organization
	 * PUT /jobs/types/{jobTypeId}/disable
	 */
	async disableJobType(
		jobTypeId: string,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const organizationId = options?.organizationId;
			const response = await apiService.put<ServiceResult>(
				`/jobs/types/${jobTypeId}/disable`,
				undefined,
				{
					headers:
						organizationId !== undefined
							? { "organization-id": organizationId }
							: undefined,
				},
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw JobApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to disable job type: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Enable a specific sub-organization's access to a job type
	 * POST /jobs/types/{jobTypeId}/sub-organization/{subOrgId}/enable
	 */
	async enableJobTypeSubOrganization(
		jobTypeId: string,
		subOrgId: string,
		options?: { organizationId?: string },
	): Promise<void> {
		const organizationId = options?.organizationId;
		await apiService.post(
			`/jobs/types/${jobTypeId}/sub-organization/${subOrgId}/enable`,
			undefined,
			{
				headers:
					organizationId !== undefined
						? { "organization-id": organizationId }
						: undefined,
			},
		);
	}

	/**
	 * Disable a specific sub-organization's access to a job type
	 * POST /jobs/types/{jobTypeId}/sub-organization/{subOrgId}/disable
	 */
	async disableJobTypeSubOrganization(
		jobTypeId: string,
		subOrgId: string,
		options?: { organizationId?: string },
	): Promise<void> {
		const organizationId = options?.organizationId;
		await apiService.post(
			`/jobs/types/${jobTypeId}/sub-organization/${subOrgId}/disable`,
			undefined,
			{
				headers:
					organizationId !== undefined
						? { "organization-id": organizationId }
						: undefined,
			},
		);
	}

	/**
	 * Delete a job type
	 * DELETE /jobs/types/{jobTypeId}
	 */
	async deleteJobType(
		jobTypeId: string,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const organizationId = options?.organizationId;
			const response = await apiService.delete<unknown>(
				`/jobs/types/${jobTypeId}`,
				{
					headers:
						organizationId && organizationId.length > 0
							? { "organization-id": organizationId }
							: undefined,
				},
			);
			if (response == null || response === "") {
				return {
					success: true,
					message: null,
					affectedId: null,
				};
			}
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw JobApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to delete job type: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Create a new job type
	 * POST /jobs/types
	 */
	async createJobType(
		data: CreateJobTypeInfo,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const validatedData = CreateJobTypeInfoSchema.parse(data);
			const organizationId = options?.organizationId;

			const response = await apiService.post<ServiceResult>(
				"/jobs/types",
				validatedData,
				{
					headers:
						organizationId && organizationId.length > 0
							? { "organization-id": organizationId }
							: undefined,
				},
			);

			const result = ServiceResultSchema.parse(response);

			if (!result.success) {
				throw JobApiError.fromServiceResult(result);
			}

			return result;
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}

			if (error instanceof z.ZodError) {
				const fieldErrors = error.issues.map((err) => ({
					field: err.path.join("."),
					message: err.message,
				}));
				throw new JobApiError(
					`Validation failed: ${fieldErrors.map((e) => `${e.field}: ${e.message}`).join(", ")}`,
					error,
				);
			}

			throw new JobApiError(
				`Failed to create job type: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Duplicate a job type. The API copies core config and appends " copy" to the name.
	 * POST /jobs/types/{jobTypeId}/duplicate
	 *
	 * Copying a real form can outlast the shared API timeout. The server still
	 * finishes, so this call waits longer and the UI can follow the real result.
	 */
	async duplicateJobType(
		jobTypeId: string,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const response = await apiService.post<ServiceResult>(
				`/jobs/types/${jobTypeId}/duplicate`,
				undefined,
				{
					headers: organizationIdHeader(options?.organizationId),
					timeout: DUPLICATE_JOB_TYPE_TIMEOUT_MS,
				},
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw JobApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to duplicate job type: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get jobs as GeoJSON features
	 * GET /jobs/features/{start}/{count}
	 */
	async getJobsFeatures(
		start: number = 1,
		count: number = 10,
		params?: JobParams,
	): Promise<FeatureCollection> {
		try {
			const response = await apiService.get<FeatureCollection>(
				`/jobs/features/${start}/${count}`,
				{
					params: toJobQueryParams(params),
					headers: organizationIdHeader(params?.OrganizationId),
				},
			);

			return FeatureCollectionSchema.parse(response);
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to get jobs features: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Export jobs to CSV
	 * GET /jobs/{start}/{count}/export
	 *
	 * @param start - Starting index (1-based)
	 * @param count - Number of jobs to export
	 * @param params - Optional query parameters (same as getJobs)
	 * @returns Blob containing CSV data
	 * @throws Error if export fails
	 */
	async exportJobs(
		start: number,
		count: number,
		params?: JobParams & { columns?: string },
	): Promise<Blob> {
		try {
			const baseParams = toJobQueryParams(params);
			const queryParams = baseParams ? { ...baseParams } : {};
			if (params && "columns" in params && params.columns !== undefined) {
				queryParams.columns = params.columns;
			}
			const response = await apiService.get<Blob>(
				`/jobs/${start}/${count}/export`,
				{
					params: queryParams,
					responseType: "blob",
					headers: {
						Accept: "text/csv",
						...organizationIdHeader(params?.OrganizationId),
					} as Record<string, string>,
				},
			);
			return response;
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to export jobs: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	// ==================== JOB ASSIGNMENTS ====================

	/**
	 * Get job assignments count
	 * GET /jobs/{jobId}/assignments/count
	 */
	async getJobAssignmentsCount(
		jobId: string,
		params?: JobAssignmentParams,
	): Promise<number> {
		try {
			const response = await apiService.get<number>(
				`/jobs/${jobId}/assignments/count`,
				{
					params,
					headers: organizationIdHeader(params?.OrganizationId),
				},
			);

			return z.number().parse(response);
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to get job assignments count: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get job assignments with pagination
	 * GET /jobs/{jobId}/assignments/{start}/{count}
	 */
	async getJobAssignments(
		jobId: string,
		start: number = 1,
		count: number = 10,
		params?: JobAssignmentParams,
	): Promise<JobAssignmentDisplay[]> {
		try {
			const response = await apiService.get<JobAssignmentDisplay[]>(
				`/jobs/${jobId}/assignments/${start}/${count}`,
				{
					params,
					headers: organizationIdHeader(params?.OrganizationId),
				},
			);

			return JobAssignmentsArray.parse(response);
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to get job assignments: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	// ==================== JOB SUB-CONTRACTORS ====================

	/**
	 * Get job sub-contractors count
	 * GET /jobs/{jobId}/sub-contractors/count
	 */
	async getJobSubContractorsCount(
		jobId: string,
		params?: SubContractorParams,
	): Promise<number> {
		try {
			const response = await apiService.get<number>(
				`/jobs/${jobId}/sub-contractors/count`,
				{
					params,
					headers: organizationIdHeader(params?.OrganizationId),
				},
			);

			return z.number().parse(response);
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to get job sub-contractors count: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get job sub-contractors with pagination
	 * GET /jobs/{jobId}/sub-contractors/{start}/{count}
	 */
	async getJobSubContractors(
		jobId: string,
		start: number = 1,
		count: number = 10,
		params?: SubContractorParams,
	): Promise<SubContractorDisplay[]> {
		try {
			const response = await apiService.get<SubContractorDisplay[]>(
				`/jobs/${jobId}/sub-contractors/${start}/${count}`,
				{
					params,
					headers: organizationIdHeader(params?.OrganizationId),
				},
			);

			return SubContractorsArray.parse(response);
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to get job sub-contractors: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	// ==================== JOB HISTORY ====================

	// ==================== JOB TYPE HISTORY ====================

	/**
	 * Get job type history count
	 * GET /jobs/types/{id}/history/count
	 */
	async getJobTypeHistoryCount(
		jobTypeId: string,
		organizationId?: string,
	): Promise<number> {
		try {
			const response = await apiService.get<number>(
				`/jobs/types/${jobTypeId}/history/count`,
				{ headers: organizationIdHeader(organizationId) },
			);
			return z.number().parse(response);
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to get job type history count: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get job type history with pagination
	 * GET /jobs/types/{id}/history/{start}/{count}
	 */
	async getJobTypeHistory(
		jobTypeId: string,
		start: number = 1,
		count: number = 10,
		organizationId?: string,
	): Promise<AuditLogDisplay[]> {
		try {
			const response = await apiService.get<AuditLogDisplay[]>(
				`/jobs/types/${jobTypeId}/history/${start}/${count}`,
				{ headers: organizationIdHeader(organizationId) },
			);
			return AuditLogArray.parse(response);
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to get job type history: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get job history count
	 * GET /jobs/{id}/history/count
	 */
	async getJobHistoryCount(jobId: string): Promise<number> {
		try {
			const response = await apiService.get<number>(
				`/jobs/${jobId}/history/count`,
			);

			return z.number().parse(response);
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to get job history count: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get job history with pagination
	 * GET /jobs/{id}/history/{start}/{count}
	 */
	async getJobHistory(
		jobId: string,
		start: number = 1,
		count: number = 10,
	): Promise<AuditLogDisplay[]> {
		try {
			const response = await apiService.get<AuditLogDisplay[]>(
				`/jobs/${jobId}/history/${start}/${count}`,
			);

			return AuditLogArray.parse(response);
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to get job history: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	// ==================== JOB ASSET ASSOCIATIONS ====================

	/**
	 * List assets linked to a job (junction rows).
	 * GET /jobs/{jobId}/assets/{start}/{count}
	 *
	 * Note: OpenAPI incorrectly documents the response as NoteDisplay[];
	 * the client parses {@link JobAssetInfo}.
	 */
	async getJobAssets(
		jobId: string,
		start: number = 1,
		count: number = 50,
		params?: JobAssetAssociationParams,
		options?: { organizationId?: string },
	): Promise<JobAssetInfo[]> {
		try {
			const response = await apiService.get<unknown>(
				`/jobs/${jobId}/assets/${start}/${count}`,
				{
					params: toJobAssociationQueryParams(params),
					headers: organizationIdHeader(
						options?.organizationId ?? params?.OrganizationId,
					),
				},
			);
			return JobAssetInfoArraySchema.parse(response);
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to get assets for job ${jobId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Count assets linked to a job.
	 * GET /jobs/{jobId}/assets/count
	 */
	async getJobAssetsCount(
		jobId: string,
		params?: JobAssetAssociationParams,
		options?: { organizationId?: string },
	): Promise<number> {
		try {
			const response = await apiService.get<number>(
				`/jobs/${jobId}/assets/count`,
				{
					params: toJobAssociationQueryParams(params),
					headers: organizationIdHeader(
						options?.organizationId ?? params?.OrganizationId,
					),
				},
			);
			return z.number().parse(response);
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to get asset count for job ${jobId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Link an existing asset to a job.
	 * POST /jobs/{jobId}/assets/{assetId}
	 */
	async linkJobAsset(
		jobId: string,
		assetId: string,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const response = await apiService.post<ServiceResult>(
				`/jobs/${jobId}/assets/${assetId}`,
				undefined,
				{
					headers: organizationIdHeader(options?.organizationId),
				},
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw JobApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to link asset ${assetId} to job ${jobId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Unlink an asset from a job.
	 * DELETE /jobs/{jobId}/assets/{assetId}
	 */
	async unlinkJobAsset(
		jobId: string,
		assetId: string,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const response = await apiService.delete<ServiceResult>(
				`/jobs/${jobId}/assets/${assetId}`,
				{
					headers: organizationIdHeader(options?.organizationId),
				},
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw JobApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to unlink asset ${assetId} from job ${jobId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	// ==================== JOB RECORD ASSOCIATIONS ====================

	/**
	 * List records linked to a job (junction rows).
	 * GET /jobs/{jobId}/records/{start}/{count}
	 *
	 * Note: OpenAPI incorrectly documents the response as NoteDisplay[];
	 * the client parses {@link JobRecordInfo}.
	 */
	async getJobRecords(
		jobId: string,
		start: number = 1,
		count: number = 50,
		params?: JobRecordAssociationParams,
		options?: { organizationId?: string },
	): Promise<JobRecordInfo[]> {
		try {
			const response = await apiService.get<unknown>(
				`/jobs/${jobId}/records/${start}/${count}`,
				{
					params: toJobAssociationQueryParams(params),
					headers: organizationIdHeader(
						options?.organizationId ?? params?.OrganizationId,
					),
				},
			);
			return JobRecordInfoArraySchema.parse(response);
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to get records for job ${jobId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Count records linked to a job.
	 * GET /jobs/{jobId}/records/count
	 */
	async getJobRecordsCount(
		jobId: string,
		params?: JobRecordAssociationParams,
		options?: { organizationId?: string },
	): Promise<number> {
		try {
			const response = await apiService.get<number>(
				`/jobs/${jobId}/records/count`,
				{
					params: toJobAssociationQueryParams(params),
					headers: organizationIdHeader(
						options?.organizationId ?? params?.OrganizationId,
					),
				},
			);
			return z.number().parse(response);
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to get record count for job ${jobId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Link an existing record to a job.
	 * POST /jobs/{jobId}/records/{recordId}
	 */
	async linkJobRecord(
		jobId: string,
		recordId: string,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const response = await apiService.post<ServiceResult>(
				`/jobs/${jobId}/records/${recordId}`,
				undefined,
				{
					headers: organizationIdHeader(options?.organizationId),
				},
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw JobApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to link record ${recordId} to job ${jobId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Unlink a record from a job.
	 * DELETE /jobs/{jobId}/records/{recordId}
	 */
	async unlinkJobRecord(
		jobId: string,
		recordId: string,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const response = await apiService.delete<ServiceResult>(
				`/jobs/${jobId}/records/${recordId}`,
				{
					headers: organizationIdHeader(options?.organizationId),
				},
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw JobApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to unlink record ${recordId} from job ${jobId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	// ==================== JOB FILES ====================

	/**
	 * List job attachments with pagination.
	 * GET /jobs/{jobId}/files/{start}/{count}
	 */
	async getJobFiles(
		jobId: string,
		start: number = 1,
		count: number = 50,
		params?: JobFileParams,
		options?: { organizationId?: string },
	): Promise<FileDisplay[]> {
		try {
			const response = await apiService.get<FileDisplay[]>(
				`/jobs/${jobId}/files/${start}/${count}`,
				{
					params: toJobFileQueryParams(params),
					headers: organizationIdHeader(
						options?.organizationId ?? params?.OrganizationId,
					),
				},
			);

			return FileDisplayArraySchema.parse(response);
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to get files for job ${jobId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Count job attachments.
	 * GET /jobs/{jobId}/files/count
	 */
	async getJobFilesCount(
		jobId: string,
		params?: JobFileParams,
		options?: { organizationId?: string },
	): Promise<number> {
		try {
			const response = await apiService.get<number>(
				`/jobs/${jobId}/files/count`,
				{
					params: toJobFileQueryParams(params),
					headers: organizationIdHeader(
						options?.organizationId ?? params?.OrganizationId,
					),
				},
			);

			return z.number().parse(response);
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to get file count for job ${jobId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Upload a job attachment.
	 * POST /jobs/{jobId}/files — multipart field name `File` (PascalCase per swagger).
	 */
	async uploadJobFile(
		jobId: string,
		file: File | Blob,
		options?: {
			organizationId?: string;
			description?: string;
			entityAttachmentRequirementTagId?: string | null;
			onUploadProgress?: (percent: number) => void;
		},
	): Promise<void> {
		try {
			const formData = new FormData();
			const filename =
				file instanceof File && file.name.length > 0 ? file.name : "upload.bin";
			formData.append("File", file, filename);
			appendAttachmentRequirementTagId(
				formData,
				options?.entityAttachmentRequirementTagId,
			);

			await apiService.post(`/jobs/${jobId}/files`, formData, {
				params: options?.description
					? { Description: options.description }
					: undefined,
				headers: {
					"Content-Type": "multipart/form-data",
					...organizationIdHeader(options?.organizationId),
				},
				onUploadProgress: (event) => {
					if (!options?.onUploadProgress || !event.total) return;
					options.onUploadProgress(
						Math.round((event.loaded / event.total) * 100),
					);
				},
			});
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to upload file for job ${jobId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get geometry source options for jobs.
	 * GET /jobs/geometry-sources
	 */
	async getGeometrySources(): Promise<GeometrySourceDisplay[]> {
		try {
			const response = await apiService.get<unknown>("/jobs/geometry-sources");
			return GeometrySourcesArraySchema.parse(response);
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to get job geometry sources: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get GPS method options for jobs.
	 * GET /jobs/gps-methods
	 */
	async getGpsMethods(): Promise<GpsMethodDisplay[]> {
		try {
			const response = await apiService.get<unknown>("/jobs/gps-methods");
			return GpsMethodsArraySchema.parse(response);
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to get job GPS methods: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Delete a job attachment.
	 * DELETE /jobs/{jobId}/files/{fileId}
	 */
	async deleteJobFile(
		jobId: string,
		fileId: string,
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			await apiService.delete(`/jobs/${jobId}/files/${fileId}`, {
				headers: organizationIdHeader(options?.organizationId),
			});
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to delete file ${fileId} for job ${jobId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Rename / update a job attachment metadata.
	 * PUT /jobs/{jobId}/files/{jobFileId}
	 */
	async editJobFile(
		jobId: string,
		jobFileId: string,
		info: EditJobFileInfo,
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			await apiService.put(`/jobs/${jobId}/files/${jobFileId}`, info, {
				headers: organizationIdHeader(options?.organizationId),
			});
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to edit file ${jobFileId} for job ${jobId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Fetch a job file as a Blob.
	 * GET /jobs/{jobId}/files/{fileId}
	 */
	async getJobFile(
		jobId: string,
		fileId: string,
		options?: { organizationId?: string },
	): Promise<Blob> {
		try {
			return await apiService.get<Blob>(`/jobs/${jobId}/files/${fileId}`, {
				responseType: "blob",
				headers: organizationIdHeader(options?.organizationId),
			});
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to get file ${fileId} for job ${jobId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Fetch a job file thumbnail as a Blob.
	 * GET /jobs/{jobId}/files/{fileId}/thumbnail
	 */
	async getJobFileThumbnail(
		jobId: string,
		fileId: string,
		options?: { organizationId?: string },
	): Promise<Blob> {
		try {
			return await apiService.get<Blob>(
				`/jobs/${jobId}/files/${fileId}/thumbnail`,
				{
					responseType: "blob",
					headers: organizationIdHeader(options?.organizationId),
				},
			);
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to get thumbnail for file ${fileId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Probe whether a job file exists (headers only).
	 * HEAD /jobs/{jobId}/files/{fileId}
	 */
	async headJobFile(
		jobId: string,
		fileId: string,
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			await apiService.head(`/jobs/${jobId}/files/${fileId}`, {
				headers: organizationIdHeader(options?.organizationId),
			});
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to head file ${fileId} for job ${jobId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Replace job file content.
	 * PATCH /jobs/{jobId}/files/{fileId} — multipart field name `file` (lowercase per swagger).
	 */
	async replaceJobFile(
		jobId: string,
		fileId: string,
		file: File | Blob,
		options?: {
			organizationId?: string;
			onUploadProgress?: (percent: number) => void;
		},
	): Promise<void> {
		try {
			const formData = new FormData();
			const filename =
				file instanceof File && file.name.length > 0 ? file.name : "upload.bin";
			formData.append("file", file, filename);

			await apiService.patch(`/jobs/${jobId}/files/${fileId}`, formData, {
				headers: {
					"Content-Type": "multipart/form-data",
					...organizationIdHeader(options?.organizationId),
				},
				onUploadProgress: (event) => {
					if (!options?.onUploadProgress || !event.total) return;
					options.onUploadProgress(
						Math.round((event.loaded / event.total) * 100),
					);
				},
			});
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to replace file ${fileId} for job ${jobId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	// ==================== JOB NOTES ====================

	/**
	 * List job notes with pagination.
	 * GET /jobs/{jobId}/notes/{start}/{count}
	 */
	async getJobNotes(
		jobId: string,
		start: number = 1,
		count: number = 50,
		params?: JobNoteParams,
		options?: { organizationId?: string },
	): Promise<NoteDisplay[]> {
		try {
			const response = await apiService.get<NoteDisplay[]>(
				`/jobs/${jobId}/notes/${start}/${count}`,
				{
					params: toJobNoteQueryParams(params),
					headers: organizationIdHeader(
						options?.organizationId ?? params?.OrganizationId,
					),
				},
			);

			return NoteDisplayArraySchema.parse(response);
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to get notes for job ${jobId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Count job notes.
	 * GET /jobs/{jobId}/notes/count
	 */
	async getJobNotesCount(
		jobId: string,
		params?: JobNoteParams,
		options?: { organizationId?: string },
	): Promise<number> {
		try {
			const response = await apiService.get<number>(
				`/jobs/${jobId}/notes/count`,
				{
					params: toJobNoteQueryParams(params),
					headers: organizationIdHeader(
						options?.organizationId ?? params?.OrganizationId,
					),
				},
			);

			return z.number().parse(response);
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to get note count for job ${jobId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Create a job note.
	 * POST /jobs/{jobId}/notes
	 */
	async createJobNote(
		jobId: string,
		info: CreateNoteInfo,
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			const body = CreateNoteInfoSchema.parse(info);
			await apiService.post(`/jobs/${jobId}/notes`, body, {
				headers: organizationIdHeader(options?.organizationId),
			});
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to create note for job ${jobId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Update a job note.
	 * PUT /jobs/{jobId}/notes/{noteId}
	 */
	async updateJobNote(
		jobId: string,
		noteId: string,
		info: CreateNoteInfo,
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			const body = CreateNoteInfoSchema.parse(info);
			await apiService.put(`/jobs/${jobId}/notes/${noteId}`, body, {
				headers: organizationIdHeader(options?.organizationId),
			});
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to update note ${noteId} for job ${jobId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Delete a job note.
	 * DELETE /jobs/{jobId}/notes/{noteId}
	 */
	async deleteJobNote(
		jobId: string,
		noteId: string,
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			await apiService.delete(`/jobs/${jobId}/notes/${noteId}`, {
				headers: organizationIdHeader(options?.organizationId),
			});
		} catch (error) {
			if (error instanceof JobApiError) {
				throw error;
			}
			throw new JobApiError(
				`Failed to delete note ${noteId} for job ${jobId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	async getJobTypeAttachmentRequirement(
		jobTypeId: string,
		options?: { organizationId?: string },
	) {
		try {
			return await getTypeAttachmentRequirement(
				"jobs",
				jobTypeId,
				options?.organizationId,
			);
		} catch (error) {
			if (error instanceof JobApiError) throw error;
			throw new JobApiError(
				`Failed to get attachment requirement for job type ${jobTypeId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	async putJobTypeAttachmentRequirement(
		jobTypeId: string,
		minimumTotalAttachments: number,
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			await putTypeAttachmentRequirement(
				"jobs",
				jobTypeId,
				minimumTotalAttachments,
				options?.organizationId,
			);
		} catch (error) {
			if (error instanceof JobApiError) throw error;
			throw new JobApiError(
				`Failed to save attachment requirement for job type ${jobTypeId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	async createJobTypeAttachmentRequirementTag(
		jobTypeId: string,
		info: {
			name?: string | null;
			description?: string | null;
			allowedKind?: string | null;
			minimumCount: number;
			sortOrder?: number | null;
		},
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			await createTypeAttachmentRequirementTag(
				"jobs",
				jobTypeId,
				info,
				options?.organizationId,
			);
		} catch (error) {
			if (error instanceof JobApiError) throw error;
			throw new JobApiError(
				`Failed to create attachment requirement tag for job type ${jobTypeId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	async editJobTypeAttachmentRequirementTag(
		jobTypeId: string,
		tagId: string,
		info: {
			name?: string | null;
			description?: string | null;
			allowedKind?: string | null;
			minimumCount: number;
			sortOrder: number;
		},
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			await editTypeAttachmentRequirementTag(
				"jobs",
				jobTypeId,
				tagId,
				info,
				options?.organizationId,
			);
		} catch (error) {
			if (error instanceof JobApiError) throw error;
			throw new JobApiError(
				`Failed to edit attachment requirement tag ${tagId} for job type ${jobTypeId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	async deleteJobTypeAttachmentRequirementTag(
		jobTypeId: string,
		tagId: string,
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			await deleteTypeAttachmentRequirementTag(
				"jobs",
				jobTypeId,
				tagId,
				options?.organizationId,
			);
		} catch (error) {
			if (error instanceof JobApiError) throw error;
			throw new JobApiError(
				`Failed to delete attachment requirement tag ${tagId} for job type ${jobTypeId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}
}

// Create and export a singleton instance
export const jobService = new JobService();
